import type { CanvasStoreContext } from '../types';
import type { ContextDelta, ExecutedCommand } from './types';

/**
 * Compute a delta between two CanvasStoreContext snapshots.
 * Uses referential equality on top-level keys for O(1) fast path.
 * Returns null if nothing changed.
 */
export function computeDelta(before: CanvasStoreContext, after: CanvasStoreContext): ContextDelta | null {
  if (before === after) return null;

  const delta: ContextDelta = {};
  let hasChange = false;

  if (before.canvas !== after.canvas) {
    delta.canvas = { before: before.canvas, after: after.canvas };
    hasChange = true;
  }
  if (before.activeLayerId !== after.activeLayerId) {
    delta.activeLayerId = { before: before.activeLayerId, after: after.activeLayerId };
    hasChange = true;
  }
  if (before.layers !== after.layers) {
    delta.layers = { before: before.layers, after: after.layers };
    hasChange = true;
  }
  if (before.nodes !== after.nodes) {
    delta.nodes = { before: before.nodes, after: after.nodes };
    hasChange = true;
  }
  if (before.connectors !== after.connectors) {
    delta.connectors = { before: before.connectors, after: after.connectors };
    hasChange = true;
  }
  if (before.groups !== after.groups) {
    delta.groups = { before: before.groups, after: after.groups };
    hasChange = true;
  }
  if (before.nodesVersion !== after.nodesVersion) {
    delta.nodesVersion = { before: before.nodesVersion, after: after.nodesVersion };
    hasChange = true;
  }
  if (before.connectorsVersion !== after.connectorsVersion) {
    delta.connectorsVersion = { before: before.connectorsVersion, after: after.connectorsVersion };
    hasChange = true;
  }
  if (before.groupsVersion !== after.groupsVersion) {
    delta.groupsVersion = { before: before.groupsVersion, after: after.groupsVersion };
    hasChange = true;
  }

  return hasChange ? delta : null;
}

/**
 * Apply a delta to a context, either forward (using 'after' values)
 * or reverse (using 'before' values).
 */
export function applyDelta(
  ctx: CanvasStoreContext,
  delta: ContextDelta,
  direction: 'forward' | 'reverse',
): CanvasStoreContext {
  const pick = direction === 'forward' ? 'after' : 'before';
  const result = { ...ctx };

  if (delta.canvas) result.canvas = delta.canvas[pick];
  if (delta.activeLayerId) result.activeLayerId = delta.activeLayerId[pick];
  if (delta.layers) result.layers = delta.layers[pick];
  if (delta.nodes) result.nodes = delta.nodes[pick];
  if (delta.connectors) result.connectors = delta.connectors[pick];
  if (delta.groups) result.groups = delta.groups[pick];
  if (delta.nodesVersion) result.nodesVersion = delta.nodesVersion[pick];
  if (delta.connectorsVersion) result.connectorsVersion = delta.connectorsVersion[pick];
  if (delta.groupsVersion) result.groupsVersion = delta.groupsVersion[pick];

  return result;
}

/**
 * Merge multiple ExecutedCommand deltas into a single delta.
 * Takes the 'before' from the first command and the 'after' from the last command for each key.
 */
export function mergeTransactionDeltas(commands: ExecutedCommand[]): ContextDelta {
  if (commands.length === 0) return {};
  if (commands.length === 1) return commands[0].delta;

  const merged: ContextDelta = {};

  const keys = ['canvas', 'activeLayerId', 'layers', 'nodes', 'connectors', 'groups', 'nodesVersion', 'connectorsVersion', 'groupsVersion'] as const;

  for (const key of keys) {
    let firstBefore: any = undefined;
    let lastAfter: any = undefined;

    for (const cmd of commands) {
      const d = cmd.delta[key];
      if (d) {
        if (firstBefore === undefined) firstBefore = d.before;
        lastAfter = d.after;
      }
    }

    if (firstBefore !== undefined) {
      (merged as any)[key] = { before: firstBefore, after: lastAfter };
    }
  }

  return merged;
}
